import { Atom, atom } from 'jotai';
import { selectAtom } from 'jotai/utils';

import {
  DisplayRef,
  Namespace,
  SequencerConfigState,
  Setting,
  SettingValue,
} from '@repo/sdk/models/components';

import { evaluateConditionExpression } from '@/features/equipment-browser/conditional-evaluator/condition-evaluator';
import {
  extractReadingsRefsFromActiveSequencerConfig,
  extractReadingsRefsFromSequencerConfigStates,
  extractSettingsRefsFromActiveSequencerConfig,
  extractSettingsRefsFromSequencerConfigStates,
} from '@/features/equipment-browser/sequencer-section/extract-sequencer-refs';
import {
  mapReadingRefsToSubscriptionIdentifiers,
  mapSettingRefsToSubscriptionIdentifiers,
} from '@/features/equipment-browser/subscriptions/map-to-subscription-identifiers';
import { EnhancedReading } from '@/features/equipment-browser/subscriptions/use-get-readings-values';
import { EnhancedSetting } from '@/features/equipment-browser/subscriptions/use-get-settings-values';
import { SubscriptionIdentifier } from '@/hooks/query-hooks/gaia-api/use-subscription-base';
import { readingsFamily } from '@/hooks/query-hooks/streams/gaia-api/use-readings-stream';
import { settingsFamily } from '@/hooks/query-hooks/streams/gaia-api/use-settings-stream';
import { LatestValue } from '@/types/reading-types';

import { buildEnhancedReading } from './display-metrics.state';
import { buildEnhancedSetting } from './display-settings.state';
import { equipmentResponseAtom } from './equipment-response.state';

/**
 * Atom that provides the list of sequencer configuration states from the equipment response
 *
 * @remarks
 * Derives sequencer states from the equipment response atom. Returns undefined if no equipment
 * response is available or if the equipment has no sequencer states.
 */
export const sequencerConfigsAtom = atom<SequencerConfigState[] | undefined>((get) => {
  const equipment = get(equipmentResponseAtom);

  return equipment?.sequencerStates;
});
sequencerConfigsAtom.debugLabel = 'sequencerConfigsAtom';

/**
 * Atom that provides subscription identifiers for all readings referenced in sequencer condition expressions
 *
 * @remarks
 * Extracts all reading references from sequencer config condition expressions and maps them to
 * subscription identifiers. These identifiers are used to subscribe to the necessary readings
 * for evaluating sequencer conditions.
 */
export const sequencerConfigsConditionReadingsIdentifiersAtom = atom<SubscriptionIdentifier[]>(
  (get) => {
    const sequencerConfigs = get(sequencerConfigsAtom);

    return (
      mapReadingRefsToSubscriptionIdentifiers(
        extractReadingsRefsFromSequencerConfigStates(sequencerConfigs),
        'sequencerConfigsConditionReadingsIdentifiersAtom'
      ) ?? []
    );
  }
);
sequencerConfigsConditionReadingsIdentifiersAtom.debugLabel =
  'sequencerConfigsConditionReadingsIdentifiersAtom';

/**
 * Atom that provides subscription identifiers for all settings referenced in sequencer condition expressions
 *
 * @remarks
 * Extracts all setting references from sequencer config condition expressions and maps them to
 * subscription identifiers. These identifiers are used to subscribe to the necessary settings
 * for evaluating sequencer conditions.
 */
export const sequencerConfigsConditionSettingsIdentifiersAtom = atom<SubscriptionIdentifier[]>(
  (get) => {
    const sequencerConfigs = get(sequencerConfigsAtom);

    return (
      mapSettingRefsToSubscriptionIdentifiers(
        extractSettingsRefsFromSequencerConfigStates(sequencerConfigs),
        'sequencerConfigsConditionSettingsIdentifiersAtom'
      ) ?? []
    );
  }
);

/**
 * Atom that provides the list of latest values for all metrics referenced in sequencer condition expressions
 *
 * @remarks
 * Extracts all metric references from sequencer config condition expressions and maps them to
 * subscription identifiers. These identifiers are used to subscribe to the necessary metrics
 * for evaluating sequencer conditions.
 */
export const activeSequencerConditionalMetricValuesListAtom = atom<LatestValue[]>((get) => {
  const metricIdentifiers: SubscriptionIdentifier[] = get(
    sequencerConfigsConditionReadingsIdentifiersAtom
  );

  return (
    metricIdentifiers?.map((reading: SubscriptionIdentifier) => {
      const latestValue = get(readingsFamily({ pointId: reading?.id } as LatestValue));

      return latestValue;
    }) ?? []
  );
});
activeSequencerConditionalMetricValuesListAtom.debugLabel =
  'activeSequencerConditionalMetricValuesListAtom';

/**
 * Atom that provides the list of latest values for all settings referenced in sequencer condition expressions
 *
 * @remarks
 * Extracts all setting references from sequencer config condition expressions and maps them to
 * subscription identifiers. These identifiers are used to subscribe to the necessary settings
 * for evaluating sequencer conditions.
 */
const activeSequencerConditionalSettingValuesListAtom = atom<SettingValue[]>((get) => {
  const settingIdentifiers: SubscriptionIdentifier[] = get(
    sequencerConfigsConditionSettingsIdentifiersAtom
  );

  return (
    settingIdentifiers?.map((setting: SubscriptionIdentifier) => {
      const settingValue = get(settingsFamily({ settingId: setting?.id } as Setting));

      return settingValue.value;
    }) ?? []
  );
});
activeSequencerConditionalSettingValuesListAtom.debugLabel =
  'activeSequencerConditionalSettingValuesListAtom';

/**
 * Atom that determines the currently active sequencer based on condition expression evaluation
 *
 * @remarks
 * Iterates through all available sequencer configs and returns the first sequencer whose
 * condition expression evaluates to true. If no sequencer conditions are met, returns undefined.
 * This enables dynamic sequencer selection based on real-time equipment state.
 */
export const activeSequencerAtom = atom<SequencerConfigState | undefined>((get) => {
  const sequencerConfigs = get(sequencerConfigsAtom);

  // Recompute activeSequencer when anything in the values arrays changes
  get(activeSequencerConditionalMetricValuesListAtom);
  get(activeSequencerConditionalSettingValuesListAtom);

  // loop through each sequencer and reduce to the first one that evaluates to true based on the below conditions
  const activeSequencer = sequencerConfigs?.reduce(
    (acc: SequencerConfigState | undefined, sequencer: SequencerConfigState) => {
      if (
        (sequencer.conditionExpression &&
          evaluateConditionExpression(sequencer.conditionExpression)) ||
        (!sequencer.conditionExpression && sequencer.isDefault)
      ) {
        return sequencer;
      }
      return acc;
    },
    undefined
  );

  return activeSequencer;
});
activeSequencerAtom.debugLabel = 'activeSequencerAtom';

/**
 * Atom that provides the list of enhanced metrics for the active sequencer's display metrics
 *
 * @remarks
 * Builds an array of enhanced metrics by combining the active sequencer's display metric
 * configurations with their latest reading values from the readings stream. Each enhanced
 * metric includes the metric configuration plus formatted value data.
 */
export const activeSequencerDisplayMetricsListAtom = atom<EnhancedReading[]>((get) => {
  const activeSequencer = get(activeSequencerAtom);

  return (
    activeSequencer?.displayMetrics
      ?.filter((metric) => metric?.ref?.namespace !== Namespace.Setting)
      .map((metric: DisplayRef) => {
        const latestValue = get(readingsFamily({ pointId: metric?.ref?.id } as LatestValue));

        return buildEnhancedReading(metric, latestValue);
      }) ?? []
  );
});
activeSequencerDisplayMetricsListAtom.debugLabel = 'activeSequencerDisplayMetricsListAtom';

/**
 * Atom that provides the list of enhanced settings for the active sequencer's display settings
 *
 * @remarks
 * Builds an array of enhanced settings by combining the active sequencer's display setting
 * configurations with their latest setting values from the settings stream. Each enhanced
 * setting includes the setting configuration plus formatted value data.
 */
export const activeSequencerDisplaySettingsListAtom = atom<EnhancedSetting[]>((get) => {
  const activeSequencer: SequencerConfigState | undefined = get(activeSequencerAtom);

  return (
    activeSequencer?.displaySettings?.map((setting: DisplayRef): EnhancedSetting => {
      const settingValue = get(settingsFamily({ settingId: setting?.ref?.id } as Setting));

      return buildEnhancedSetting(setting, settingValue);
    }) ?? []
  );
});
activeSequencerDisplayMetricsListAtom.debugLabel = 'activeSequencerDisplayMetricsListAtom';

/**
 * Atom that provides subscription identifiers for readings in the active sequencer's display metrics
 *
 * @remarks
 * Extracts reading references from the active sequencer's configuration and maps them to
 * subscription identifiers with the context key 'active-sequencer-reading'. These identifiers
 * are used to subscribe to the necessary readings for displaying the active sequencer's metrics.
 */
export const activeSequencerReadingsIdentifiersAtom = atom<SubscriptionIdentifier[]>((get) => {
  const activeSequencer = get(activeSequencerAtom);
  return mapReadingRefsToSubscriptionIdentifiers(
    extractReadingsRefsFromActiveSequencerConfig(activeSequencer),
    'active-sequencer-reading'
  );
});
activeSequencerReadingsIdentifiersAtom.debugLabel = 'activeSequencerReadingsIdentifiersAtom';

/**
 * Atom that provides subscription identifiers for settings in the active sequencer's display settings
 *
 * @remarks
 * Extracts setting references from the active sequencer's configuration and maps them to
 * subscription identifiers with the context key 'active-sequencer-setting'. These identifiers
 * are used to subscribe to the necessary settings for displaying the active sequencer's settings.
 */
export const activeSequencerSettingsIdentifiersAtom = atom<SubscriptionIdentifier[]>((get) => {
  const activeSequencer = get(activeSequencerAtom);
  return mapSettingRefsToSubscriptionIdentifiers(
    extractSettingsRefsFromActiveSequencerConfig(activeSequencer),
    'active-sequencer-setting'
  );
});
activeSequencerSettingsIdentifiersAtom.debugLabel = 'activeSequencerSettingsIdentifiersAtom';

/**
 * Factory function that creates an atom for retrieving a specific metric from the active sequencer
 *
 * @param id - The unique identifier of the metric to retrieve
 * @returns A select atom that returns the matching enhanced metric and only re-renders when
 *          the metric's value or label changes
 *
 * @remarks
 * This function creates a memoized atom that:
 * 1. Finds the metric with the specified ID from the active sequencer's display metrics
 * 2. Uses selectAtom to optimize re-renders by only updating when the value or label changes
 * 3. Provides efficient subscriptions to individual metrics without triggering unnecessary updates
 */
export const getActiveSequencerDisplayMetricAtom = (
  id: string
): Atom<EnhancedReading | undefined> => {
  const metricAtom = atom<EnhancedReading | undefined>((get) => {
    const metrics = get(activeSequencerDisplayMetricsListAtom);

    return metrics?.find((metric: EnhancedReading) => metric?.ref?.id === id);
  });

  return selectAtom<EnhancedReading | undefined, EnhancedReading | undefined>(
    metricAtom,
    (atom) => atom,
    (a: EnhancedReading | undefined, b: EnhancedReading | undefined) => {
      return a?.value === b?.value && a?.label === b?.label;
    }
  );
};
getActiveSequencerDisplayMetricAtom.debugLabel = 'getActiveSequencerDisplayMetricAtom';

/**
 * Factory function that creates an atom for retrieving a specific setting from the active sequencer's display settings
 *
 * @param id - The unique identifier of the setting to retrieve
 * @returns A select atom that returns the matching enhanced setting and only re-renders when
 *          the setting's value or label changes
 */
export const getActiveSequencerDisplaySettingAtom = (
  id: string
): Atom<EnhancedSetting | undefined> => {
  const settingAtom = atom<EnhancedSetting | undefined>((get) => {
    const settings = get(activeSequencerDisplaySettingsListAtom);

    return settings?.find((setting: EnhancedSetting) => setting?.ref?.id === id);
  });

  return selectAtom<EnhancedSetting | undefined, EnhancedSetting | undefined>(
    settingAtom,
    (atom) => atom,
    (a: EnhancedSetting | undefined, b: EnhancedSetting | undefined) => {
      return a?.value === b?.value && a?.label === b?.label;
    }
  );
};
getActiveSequencerDisplaySettingAtom.debugLabel = 'getActiveSequencerDisplaySettingAtom';
